import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { FiMenu, FiX, FiChevronDown, FiMaximize, FiMinimize } from 'react-icons/fi';
import { FaGraduationCap } from 'react-icons/fa';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [, forceUpdate] = useState();
  const menuRef = useRef(null);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    forceUpdate({});
  }, [location, user]);

  useEffect(() => {
    const handleAuthChange = () => forceUpdate({});
    window.addEventListener('authStateChanged', handleAuthChange);
    return () => {
      window.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleProfileMenu = () => setIsProfileMenuOpen(!isProfileMenuOpen);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((e) => {
        console.error(`Error attempting to enable full-screen mode: ${e.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullScreen(!isFullScreen);
  };

  const getDashboardLink = () => {
    if (!user) return '/dashboard';
    switch(user.userType) {
      case 'alumni':
        return '/dashboard';
      case 'institute':
        return '/instituteDashboard';
      case 'employer':
        return '/employerDashboard';
      case 'student':
        return '/studentDashboard';
      case 'faculty':
        return '/facultyDashboard';
        case 'admin':
          return '/admin';
      default:
        return '/dashboard';
    }
  };

  const closeMenus = () => {
    setIsMenuOpen(false);
    setIsProfileMenuOpen(false);
  };

  return (
    <header className="bg-white text-gray-800 shadow-md">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 text-2xl font-bold tracking-tight text-indigo-600 hover:text-indigo-700 transition-colors" onClick={closeMenus}>
            <FaGraduationCap className="text-3xl" />
            <span>SAARTHI</span>
          </Link>
          <nav className="hidden lg:flex items-center space-x-6">
            <ul className="flex space-x-6 items-center">
              <NavItem to="/directory" label="Alumni Directory" onClick={closeMenus} />
              <NavItem to="/jobs" label="Job Board" onClick={closeMenus} />
              <NavItem to="/events" label="Events" onClick={closeMenus} />
              <NavItem to="/campaigns" label="Campaigns" onClick={closeMenus} />
              <NavItem to="/crd" label="CRD" onClick={closeMenus} />
              <NavItem to="/manzil" label="Manzil" onClick={closeMenus} />
              {user ? (
                <>
                  <NavItem to={getDashboardLink()} label="Dashboard" onClick={closeMenus} />
                  <li className="relative" ref={profileMenuRef}>
                    <button
                      onClick={toggleProfileMenu}
                      className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                    >
                      <span>{user.name}</span>
                      <FiChevronDown />
                    </button>
                    {isProfileMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                        <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 transition-colors">Logout</button>
                      </div>
                    )}
                  </li>
                </>
              ) : (
                <>
                  <li><AuthButton to="/login" label="Login" onClick={closeMenus} /></li>
                  <li><AuthButton to="/signup" label="Sign Up" variant="outline" onClick={closeMenus} /></li>
                </>
              )}
            </ul>
            <button
              onClick={toggleFullScreen}
              className="text-gray-600 hover:text-indigo-600 transition-colors"
              aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
            >
              {isFullScreen ? <FiMinimize size={24} /> : <FiMaximize size={24} />}
            </button>
          </nav>
          <div className="lg:hidden flex items-center space-x-4">
            <button
              onClick={toggleFullScreen}
              className="text-gray-600 hover:text-indigo-600 transition-colors"
              aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
            >
              {isFullScreen ? <FiMinimize size={24} /> : <FiMaximize size={24} />}
            </button>
            <button
              className="text-gray-600 hover:text-indigo-600 transition-colors"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </div>
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200" ref={menuRef}>
          <nav className="container mx-auto px-4 py-3">
            <ul className="space-y-2">
              <MobileNavItem to="/directory" label="Alumni Directory" onClick={closeMenus} />
              <MobileNavItem to="/jobs" label="Job Board" onClick={closeMenus} />
              <MobileNavItem to="/events" label="Events" onClick={closeMenus} />
              <MobileNavItem to="/campaigns" label="Campaigns" onClick={closeMenus} />
              <MobileNavItem to="/crd" label="CRD" onClick={closeMenus} />
              <MobileNavItem to="/manzil" label="Manzil" onClick={closeMenus} />
              {user ? (
                <>
                  <MobileNavItem to={getDashboardLink()} label="Dashboard" onClick={closeMenus} />
                  <li>
                    <button onClick={() => { handleLogout(); closeMenus(); }} className="w-full text-left px-2 py-1 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <MobileNavItem to="/login" label="Login" onClick={closeMenus} />
                  <MobileNavItem to="/signup" label="Sign Up" onClick={closeMenus} />
                </>
              )}
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

const NavItem = ({ to, label, onClick }) => (
  <li>
    <Link to={to} className="text-gray-600 hover:text-indigo-600 transition-colors" onClick={onClick}>
      {label}
    </Link>
  </li>
);

const MobileNavItem = ({ to, label, onClick }) => (
  <li>
    <Link to={to} className="block px-2 py-1 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" onClick={onClick}>
      {label}
    </Link>
  </li>
);

const AuthButton = ({ to, label, variant = "default", onClick }) => (
  <Link
    to={to}
    className={`px-4 py-2 rounded-full transition-colors ${
      variant === "outline"
        ? "border border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white"
        : "bg-indigo-600 text-white hover:bg-indigo-700"
    }`}
    onClick={onClick}
  >
    {label}
  </Link>
);

export default Header;